import React, {
  ElementType,
  HTMLAttributes,
  JSXElementConstructor,
  ReactNode,
  SyntheticEvent
} from 'react'
import {
  Autocomplete as MUIAutocomplete,
  AutocompleteRenderOptionState,
  AutocompleteRenderGetTagProps,
  PopperProps
} from '@mui/material'
import ListboxComponent from './ListBoxComponent'
import { useTranslation } from 'language/translator'

export interface RenderInputParams {
  disabled: boolean
  InputProps: any
  InputLabelProps: any
}

interface AutocompleteProps {
  Autocomplete?: boolean
  autoHighlight?: boolean
  autoSelect?: boolean
  blurOnSelect?: boolean
  ChipProps?: object
  clearIcon?: ReactNode
  clearOnBlur?: boolean
  clearOnEscape?: boolean
  clearText?: string
  closeText?: string
  defaultValue?: any
  disabled?: boolean
  disableClearable?: boolean
  disableCloseOnSelect?: boolean
  filterSelectedOptions?: boolean
  forcePopupIcon?: 'auto' | boolean
  freeSolo?: boolean
  fullWidth?: boolean
  // eslint-disable-next-line no-unused-vars
  getOptionLabel?: (option: any) => string
  groupBy?: (option: any) => string
  id?: string
  inputValue?: string
  // eslint-disable-next-line no-unused-vars
  isOptionEqualToValue?: (option: any, value: any) => boolean
  limitTags?: number
  ListboxItemHeight?: number
  ListboxProps?: object
  loading?: boolean
  loadingText?: ReactNode
  multiple?: boolean
  noOptionsText?: ReactNode
  onChange?: (event: SyntheticEvent, value: any) => void
  onClose?: (event: SyntheticEvent, reason: string) => void
  onInputChange?: (event: SyntheticEvent, value: any, reason: string) => void
  onOpen?: () => void
  openText?: string
  openOnFocus?: boolean
  options: any[]
  PaperComponent?: JSXElementConstructor<HTMLAttributes<HTMLElement>>
  PopperComponent?: ElementType<PopperProps>
  popupIcon?: ReactNode
  readOnly?: boolean
  // eslint-disable-next-line no-unused-vars
  renderInput: (params: RenderInputParams) => ReactNode
  // eslint-disable-next-line no-unused-vars
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: any
    },
    option: any,
    state: AutocompleteRenderOptionState
  ) => ReactNode
  renderTags?: (
    value: any[],
    getTagProps: AutocompleteRenderGetTagProps,
    ownerState: object
  ) => React.ReactNode
  size?: 'small' | 'medium'
  sx?: object
  value?: any
}

/**
 * The Autocomplete component let users enter and
 * edit text.
 *
 * It acts as a wrapper component for a
 * complete form control including a label,
 * input, and help text.
 */
const Autocomplete = React.forwardRef((props: AutocompleteProps, ref: any) => {
  const { ListboxItemHeight, ...rest } = props

  const { t } = useTranslation()

  return (
    <>
      <MUIAutocomplete
        {...rest}
        ref={ref}
        groupBy={
          props.options && props.options.length > 0 && props.options[0].group
            ? (option) => option.group
            : undefined
        }
        componentsProps={{
          popper: { sx: { zIndex: 1600 } }
        }}
        renderGroup={(params) => params as any}
        disableListWrap
        noOptionsText={t('global', 'no_options_found')}
        ListboxComponent={
          ListboxComponent as React.ComponentType<
            React.HTMLAttributes<HTMLElement>
          >
        }
        ListboxProps={
          {
            itemHeight: ListboxItemHeight
          } as unknown as React.HTMLAttributes<HTMLUListElement>
        }
      />
    </>
  )
})

Autocomplete.displayName = 'Autocomplete'
export default Autocomplete
